import type { DocumentRef } from '../../lib/documentReader'
import {
  scopeFromParams,
  isBookDiscoveryCard,
  workbenchHref,
  type ViewScope,
  type ContextWork,
} from '../navigation/context'
import { contextWorkView, tasksForQueue } from './contextWork'

export interface RecoveryProposalGroup {
  group_id: string
  proposal_ids: readonly string[]
}
export interface RecoveryDestination {
  kind: 'book' | 'evidence' | 'chase' | 'document' | 'fund_overview' | 'admin'
  target_id: string
  href: string | null
}
export interface RecoveryItem {
  task_id: string
  task_type: string
  source_question_kind: 'multiclass' | 'source_questions' | null
  status: 'open' | 'in_progress' | 'pending_send' | 'snoozed' | 'resolved' | 'cancelled'
  origin_status: string | null
  label: string
  created_at: string
  updated_at: string
  subject: { kind: string; id: string; label: string | null } | null
  proposal_group_id: string | null
  proposal_group_ids: readonly string[]
  proposal_groups: readonly RecoveryProposalGroup[]
  proposal_selection_scope: 'task' | 'view'
  has_other_context_proposals: boolean
  documents: readonly DocumentRef[]
  source_ids: readonly string[]
  client_ids: readonly string[]
  fund_context_ids: readonly string[]
  record_fund_ids: readonly string[]
  destinations: readonly RecoveryDestination[]
  read_revision: string
}
export interface RecoveryPage {
  scope: ViewScope
  items: readonly RecoveryItem[]
  total: number
  next_cursor: string | null
  read_only: true
}
export interface RecoveryOutcome {
  task_id: string
  status: string
  outcome: 'requeued' | 'succeeded' | 'partial' | 'dead_lettered_again' | 'resolution_question'
  retried_event_id: string | null
  written_count: number | null
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const STATES = ['open', 'in_progress', 'pending_send', 'snoozed', 'resolved', 'cancelled']
const SCOPE_KEYS = ['client_id', 'fund_id', 'subchain_id', 'below_entity_id'] as const
const KINDS = ['book', 'evidence', 'chase', 'document', 'fund_overview', 'admin']
function invalid(): never {
  throw new Error('The current work response could not be verified. Refresh this view.')
}
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid()
  return value as Record<string, unknown>
}
function id(value: unknown): string {
  if (typeof value !== 'string' || !UUID.test(value)) return invalid()
  return value
}
function string(value: unknown): string {
  if (typeof value !== 'string') return invalid()
  return value
}
function ids(value: unknown): string[] {
  if (!Array.isArray(value)) return invalid()
  const values = value.map(id)
  if (new Set(values).size !== values.length) return invalid()
  return values
}
function optionalString(value: unknown): string | null {
  return value == null ? null : string(value)
}
export function sameRecoveryScope(left: ViewScope, right: ViewScope): boolean {
  return (
    left.kind === right.kind &&
    SCOPE_KEYS.every((key) => (left[key] ?? null) === (right[key] ?? null))
  )
}
export function recoveryQuery(
  scope: ViewScope,
  options: { taskId?: string; cursor?: string | null } = {}
): string {
  // Reuse the same scope validation and names as the existing Record routes.
  workbenchHref(scope)
  const query = new URLSearchParams({ scope: scope.kind })
  for (const key of SCOPE_KEYS) if (scope[key]) query.set(key, scope[key]!)
  if (options.cursor) query.set('cursor', id(options.cursor))
  return '/api/navigation/recovery' + (options.taskId ? '/' + id(options.taskId) : '') + '?' + query
}
export function recoveryHref(
  scope: ViewScope,
  taskId?: string,
  queue: 'book' | 'evidence' | 'chase' = 'book'
): string {
  const url = new URL(workbenchHref(scope, queue), 'https://nolos.invalid')
  url.searchParams.set('recovery', '1')
  if (taskId) url.searchParams.set('task', id(taskId))
  return url.pathname + url.search + '#additional-work'
}
function destination(raw: unknown, row: RecoveryItem, scope: ViewScope): RecoveryDestination {
  const data = object(raw)
  const kind = string(data.kind) as RecoveryDestination['kind']
  if (!KINDS.includes(kind)) return invalid()
  const target = id(data.target_id)
  const href = optionalString(data.href)
  if (kind === 'admin') {
    if (target !== row.task_id || href !== null) return invalid()
  } else if (kind === 'document') {
    if (
      !row.documents.some((doc) => doc.document_id === target) ||
      href !== `/v2/documents/${target}`
    )
      return invalid()
  } else if (kind === 'fund_overview') {
    if (!row.fund_context_ids.includes(target) || href !== `/v2/funds/${target}`) return invalid()
  } else {
    if (!href || !href.startsWith('/v2/workbench?') || href.includes('\\')) return invalid()
    const url = new URL(href, 'https://nolos.invalid')
    if (
      url.origin !== 'https://nolos.invalid' ||
      url.pathname !== '/v2/workbench' ||
      url.hash ||
      url.searchParams.get('task') !== row.task_id ||
      url.searchParams.get('queue') !== kind ||
      !sameRecoveryScope(scopeFromParams(url.searchParams), scope)
    )
      return invalid()
    const recovery = url.searchParams.getAll('recovery')
    if (recovery.length && (recovery.length !== 1 || recovery[0] !== '1')) return invalid()
    const group = url.searchParams.get('group')
    if (
      group ? !row.proposal_group_ids.includes(group) || target !== group : target !== row.task_id
    )
      return invalid()
    for (const key of url.searchParams.keys())
      if (
        ![
          'scope',
          'queue',
          'task',
          'group',
          'fund',
          'client',
          'subchain',
          'below',
          'recovery',
        ].includes(key)
      )
        return invalid()
  }
  return { kind, target_id: target, href }
}
export function parseRecoveryItem(
  raw: unknown,
  scope: ViewScope,
  expectedTask?: string
): RecoveryItem {
  const data = object(raw)
  const taskId = id(data.task_id)
  if (expectedTask && taskId !== expectedTask) return invalid()
  const status = string(data.status) as RecoveryItem['status']
  if (!STATES.includes(status) || data.task_type === 'daily_digest') return invalid()
  if (typeof data.read_revision !== 'string' || !/^[a-f0-9]{64}$/.test(data.read_revision))
    return invalid()
  if (!Array.isArray(data.documents) || !Array.isArray(data.destinations)) return invalid()
  const documents = data.documents.map((value): DocumentRef => {
    const doc = object(value)
    const page = doc.page == null ? null : doc.page
    if (page !== null && (!Number.isSafeInteger(page) || (page as number) < 1)) return invalid()
    return {
      document_id: id(doc.document_id),
      display_title: optionalString(doc.display_title),
      page: page as number | null,
    }
  })
  if (new Set(documents.map((doc) => doc.document_id)).size !== documents.length) return invalid()
  const groupIds = ids(data.proposal_group_ids)
  const groupId = data.proposal_group_id == null ? null : id(data.proposal_group_id)
  if (groupId !== (groupIds.length === 1 ? groupIds[0] : null)) return invalid()
  if (!Array.isArray(data.proposal_groups)) return invalid()
  const seenProposals = new Set<string>()
  const groups = data.proposal_groups.map((value) => {
    const group = object(value)
    const group_id = id(group.group_id),
      proposal_ids = ids(group.proposal_ids)
    if (!groupIds.includes(group_id) || !proposal_ids.length) return invalid()
    for (const proposal of proposal_ids) {
      if (seenProposals.has(proposal)) return invalid()
      seenProposals.add(proposal)
    }
    return { group_id, proposal_ids }
  })
  if (new Set(groups.map((group) => group.group_id)).size !== groups.length) return invalid()
  if (
    !['task', 'view'].includes(String(data.proposal_selection_scope)) ||
    typeof data.has_other_context_proposals !== 'boolean'
  )
    return invalid()
  const subject = data.subject == null ? null : object(data.subject)
  if (
    data.source_question_kind != null &&
    !['multiclass', 'source_questions'].includes(String(data.source_question_kind))
  )
    return invalid()
  if (
    data.source_question_kind === 'multiclass' &&
    (data.task_type !== 'dead_letter_review' || subject?.kind !== 'document')
  )
    return invalid()
  if (
    data.source_question_kind === 'source_questions' &&
    (data.task_type !== 'source_question' || subject?.kind !== 'document')
  )
    return invalid()
  const row: RecoveryItem = {
    task_id: taskId,
    task_type: string(data.task_type),
    source_question_kind:
      data.source_question_kind === 'source_questions'
        ? 'source_questions'
        : data.source_question_kind === 'multiclass'
          ? 'multiclass'
          : null,
    status,
    origin_status: optionalString(data.origin_status),
    label: string(data.label),
    created_at: string(data.created_at),
    updated_at: string(data.updated_at),
    subject: subject
      ? { kind: string(subject.kind), id: id(subject.id), label: optionalString(subject.label) }
      : null,
    proposal_group_id: groupId,
    proposal_group_ids: groupIds,
    proposal_groups: groups,
    documents,
    proposal_selection_scope: data.proposal_selection_scope as 'task' | 'view',
    has_other_context_proposals: data.has_other_context_proposals,
    source_ids: ids(data.source_ids),
    client_ids: ids(data.client_ids),
    fund_context_ids: ids(data.fund_context_ids),
    record_fund_ids: ids(data.record_fund_ids),
    destinations: [],
    read_revision: data.read_revision,
  }
  if (!Number.isFinite(Date.parse(row.created_at)) || !Number.isFinite(Date.parse(row.updated_at)))
    return invalid()
  row.destinations = data.destinations.map((value) => destination(value, row, scope))
  return row
}
export function parseRecoveryPage(
  raw: unknown,
  scope: ViewScope,
  cursor: string | null = null
): RecoveryPage {
  const data = object(raw)
  if (
    !sameRecoveryScope(object(data.scope) as unknown as ViewScope, scope) ||
    data.read_only !== true ||
    !Number.isSafeInteger(data.total) ||
    (data.total as number) < 0 ||
    !Array.isArray(data.items)
  )
    return invalid()
  const items = data.items.map((row) => parseRecoveryItem(row, scope))
  if (
    new Set(items.map((row) => row.task_id)).size !== items.length ||
    (data.total as number) < items.length
  )
    return invalid()
  const next = data.next_cursor == null ? null : id(data.next_cursor)
  if (
    (!cursor && items.length === 0 && data.total !== 0) ||
    (next && (next === cursor || items[items.length - 1]?.task_id !== next))
  )
    return invalid()
  return { scope, items, total: data.total as number, next_cursor: next, read_only: true }
}
/** Use only destinations already verified by parseRecoveryItem for this scope/account.
 * Recovery with Book proposals or its own Retry action keeps its focused review.
 */
export function recoveryQueueDestination(row: RecoveryItem): RecoveryDestination | null {
  if (
    !['open', 'in_progress', 'pending_send'].includes(row.status) ||
    row.proposal_group_ids.length ||
    offersWholeRetry(row)
  )
    return null
  const queues = row.destinations.filter((door) =>
    ['book', 'evidence', 'chase'].includes(door.kind)
  )
  const door = queues.length === 1 ? queues[0] : null
  return door?.href && (door.kind === 'evidence' || door.kind === 'chase') ? door : null
}
/** Discovery only. The existing writer independently admits actor/source/task. */
export function offersWholeRetry(row: RecoveryItem): boolean {
  if (!['open', 'in_progress'].includes(row.status)) return false
  return (
    (row.task_type === 'document_stuck' && row.origin_status === 'dead_letter') ||
    (row.task_type === 'dead_letter_review' && !!row.proposal_group_id)
  )
}
export function parseRecoveryOutcome(raw: unknown, taskId: string): RecoveryOutcome {
  const row = object(raw)
  if (
    row.task_id !== taskId ||
    !['requeued', 'succeeded', 'partial', 'dead_lettered_again', 'resolution_question'].includes(
      String(row.outcome)
    )
  )
    return invalid()
  const live = row.outcome === 'dead_lettered_again'
  if (live ? !['open', 'in_progress'].includes(String(row.status)) : row.status !== 'resolved')
    return invalid()
  const eventId = row.retried_event_id == null ? null : id(row.retried_event_id)
  if (row.outcome === 'requeued' && !eventId) return invalid()
  const count = row.written_count == null ? null : row.written_count
  if (count !== null && (!Number.isSafeInteger(count) || (count as number) < 0)) return invalid()
  return {
    task_id: taskId,
    status: String(row.status),
    outcome: row.outcome as RecoveryOutcome['outcome'],
    retried_event_id: eventId,
    written_count: count as number | null,
  }
}
export function recoveryOutcomeText(outcome: RecoveryOutcome): string {
  switch (outcome.outcome) {
    case 'requeued':
      return 'Retry queued. Processing has not completed yet.'
    case 'succeeded':
      return outcome.written_count === 0
        ? 'The write was already recorded.'
        : 'Recording completed.'
    case 'partial':
      return 'Recording completed for the accepted changes.'
    case 'dead_lettered_again':
      return 'The write failed again. This item remains open.'
    case 'resolution_question':
      return 'Back in Book review. Review the outstanding question.'
  }
}
export function recoveryStatus(status: RecoveryItem['status']): string {
  return {
    open: 'Open',
    in_progress: 'In progress',
    pending_send: 'Pending',
    snoozed: 'Parked',
    resolved: 'Resolved',
    cancelled: 'Cancelled',
  }[status]
}

export function representedRecoveryTasks(work: ContextWork | null): Set<string> {
  const view = contextWorkView(work)
  return new Set([
    ...view.cards.flatMap((card) =>
      (['book', 'evidence', 'chase'] as const).flatMap((queue) =>
        tasksForQueue(card, queue).map((task) => task.id)
      )
    ),
    ...view.cards.flatMap((item) =>
      isBookDiscoveryCard(item.card) ? (item.card.source_question_ids ?? []) : []
    ),
  ])
}

/** A selected task absent from queue decisions belongs to the admitted recovery read,
 * including after its URL has been normalized to the owning queue.
 */
export function recoveryOwnsSelection(
  work: ContextWork | null,
  focused: boolean,
  taskId: string | null
): boolean {
  // Old task links still open the owning document card after this move.
  if (
    taskId &&
    contextWorkView(work).cards.some(
      (item) => isBookDiscoveryCard(item.card) && item.card.source_question_ids?.includes(taskId)
    )
  )
    return false
  return focused || (!!taskId && !representedRecoveryTasks(work).has(taskId))
}
/** Already visible queue decisions are not counted again as additional work. */
export function additionalRecoveryItems(
  rows: readonly RecoveryItem[],
  work: ContextWork | null,
  selectedId: string | null
): readonly RecoveryItem[] {
  if (!work) return rows
  const represented = representedRecoveryTasks(work)
  const sourceQuestions = new Set(
    contextWorkView(work).cards.flatMap((item) =>
      isBookDiscoveryCard(item.card) ? (item.card.source_question_ids ?? []) : []
    )
  )
  const proposals = new Set([
    ...contextWorkView(work).cards.flatMap((card) => card.proposals.map((proposal) => proposal.id)),
    ...(work.book_cards ?? []).flatMap((card) => [...card.proposal_ids]),
  ])
  return rows.filter((row) => {
    if (sourceQuestions.has(row.task_id)) return false
    if (row.task_id === selectedId) return true
    if (represented.has(row.task_id)) return false
    const selected = row.proposal_groups.flatMap((group) => [...group.proposal_ids])
    return (
      row.task_type !== 'proposal_review' ||
      selected.length === 0 ||
      selected.some((id) => !proposals.has(id))
    )
  })
}
